import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
  confirmCatalogImport,
  importCatalogPreview,
  importCatalogPreviewRows,
} from '../api/catalogImport'
import {
  CATALOG_IMPORT_FIELDS,
  catalogImportFieldLabel,
  columnMappingFromPreview,
  importMatchDecisionLabel,
  mappingSourceLabel,
  type CatalogImportConfirmResultDto,
  type CatalogImportPreviewDto,
  type ColumnMapping,
  type ImportPreviewRowDto,
} from '../api/catalogImportTypes'
import { matchRestaurantPrivateSupplier } from '../api/myPrices'
import { listProductTypes, type ProductTypeDto } from '../api/productTypes'
import type { ImportStartMode } from './PricesScreen'
import { isValidInn } from '../utils/inn'

type ImportWizardScreenProps = {
  organizationId: number
  mode: ImportStartMode
  onClose: () => void
  onDone: () => void
}

type WizardStep = 'company' | 'file' | 'columns' | 'review' | 'confirm' | 'result'

const ACCEPT_FILES = '.xlsx,.xls,.csv,.pdf,image/*'

const getFlow = (mode: ImportStartMode): WizardStep[] => {
  if (mode.kind === 'new') {
    return ['company', 'file', 'columns', 'review', 'confirm', 'result']
  }
  return ['file', 'columns', 'review', 'confirm', 'result']
}

const targetLabel = (
  mode: ImportStartMode,
  company: { legalName: string; displayLabel: string },
): string => {
  if (mode.kind === 'update') {
    return mode.supplierName
  }
  return company.displayLabel.trim() || company.legalName.trim() || 'Новый поставщик'
}

const proposedSourceForField = (
  preview: CatalogImportPreviewDto | null,
  field: string,
): string | null => {
  if (!preview) {
    return null
  }
  return preview.proposedMapping.find((entry) => entry.field === field)?.source ?? null
}

const effectiveDecision = (
  row: ImportPreviewRowDto,
  overrides: Record<number, number>,
  excluded: Set<number>,
): string => {
  if (excluded.has(row.rowNumber)) {
    return 'excluded'
  }
  if (overrides[row.rowNumber] != null) {
    return 'manual'
  }
  return row.matchPreview.decision.toLowerCase()
}

const rowCategoryLabel = (
  row: ImportPreviewRowDto,
  overrides: Record<number, number>,
  productTypes: ProductTypeDto[],
): string | null => {
  const overrideId = overrides[row.rowNumber]
  if (overrideId != null) {
    return productTypes.find((type) => type.id === overrideId)?.name ?? `Категория #${overrideId}`
  }
  const top = row.matchPreview.candidates[0]
  if (!top) {
    return null
  }
  const decision = row.matchPreview.decision.toLowerCase()
  if (decision === 'auto' || decision === 'suggested' || decision === 'manual') {
    return top.name
  }
  return null
}

export const ImportWizardScreen = ({
  organizationId,
  mode,
  onClose,
  onDone,
}: ImportWizardScreenProps) => {
  const flow = useMemo(() => getFlow(mode), [mode])
  const [stepIndex, setStepIndex] = useState(0)
  const step = flow[stepIndex] ?? 'result'

  const [inn, setInn] = useState('')
  const [legalName, setLegalName] = useState('')
  const [displayLabel, setDisplayLabel] = useState('')
  const [matchMessage, setMatchMessage] = useState<string | null>(null)
  const [matchTone, setMatchTone] = useState<'ok' | 'warn' | null>(null)
  const [matchLoading, setMatchLoading] = useState(false)

  const [preview, setPreview] = useState<CatalogImportPreviewDto | null>(null)
  const [columnMapping, setColumnMapping] = useState<ColumnMapping>({})
  const [rows, setRows] = useState<ImportPreviewRowDto[]>([])
  const [rowProductTypeOverrides, setRowProductTypeOverrides] = useState<Record<number, number>>({})
  const [excludedRowNumbers, setExcludedRowNumbers] = useState<Set<number>>(new Set())
  const [confirmResult, setConfirmResult] = useState<CatalogImportConfirmResultDto | null>(null)

  const [productTypes, setProductTypes] = useState<ProductTypeDto[]>([])
  const [categoryPickerRow, setCategoryPickerRow] = useState<number | null>(null)
  const [categorySearch, setCategorySearch] = useState('')

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fileInputRef = useRef<HTMLInputElement>(null)

  const company = { legalName, displayLabel }
  const catalogName = targetLabel(mode, company)
  const excludedSet = excludedRowNumbers

  const activeRows = useMemo(
    () => rows.filter((row) => !excludedSet.has(row.rowNumber)),
    [excludedSet, rows],
  )

  const matchedCount = useMemo(
    () =>
      activeRows.filter((row) => {
        const decision = effectiveDecision(row, rowProductTypeOverrides, excludedSet)
        return decision === 'auto' || decision === 'manual' || decision === 'suggested'
      }).length,
    [activeRows, excludedSet, rowProductTypeOverrides],
  )

  const stepsBeforeResult = flow.length - 1

  useEffect(() => {
    void listProductTypes()
      .then(setProductTypes)
      .catch(() => {
        // Category picker degrades gracefully if types fail to load.
      })
  }, [])

  const goBack = () => {
    if (stepIndex <= 0) {
      onClose()
      return
    }
    setStepIndex((current) => current - 1)
    setError(null)
  }

  const goNext = () => {
    setStepIndex((current) => Math.min(current + 1, flow.length - 1))
    setError(null)
  }

  const handleCheckInn = async () => {
    if (!isValidInn(inn)) {
      setMatchMessage('Введите корректный ИНН: 10 или 12 цифр')
      setMatchTone('warn')
      return
    }
    setMatchLoading(true)
    setMatchMessage(null)
    setMatchTone(null)
    try {
      const match = await matchRestaurantPrivateSupplier(organizationId, inn, legalName || undefined)
      if (match.linkedSupplierName) {
        setLegalName(match.linkedSupplierName)
        setMatchMessage(`Найден поставщик в системе: ${match.linkedSupplierName}`)
        setMatchTone('ok')
      } else if (match.existingPrivateSupplierId != null) {
        setMatchMessage('Такой поставщик уже есть в ваших прайсах — можно продолжить импорт.')
        setMatchTone('ok')
      } else {
        setMatchMessage('Поставщик не найден в системе — будет создан новый профиль.')
        setMatchTone('warn')
      }
    } catch (checkError) {
      setMatchMessage(
        checkError instanceof Error ? checkError.message : 'Не удалось проверить ИНН',
      )
      setMatchTone('warn')
    } finally {
      setMatchLoading(false)
    }
  }

  const handleFileSelected = async (file: File | null) => {
    if (!file) {
      return
    }
    setLoading(true)
    setError(null)
    try {
      const nextPreview = await importCatalogPreview(organizationId, file)
      setPreview(nextPreview)
      setColumnMapping(columnMappingFromPreview(nextPreview))
      setRows([])
      setRowProductTypeOverrides({})
      setExcludedRowNumbers(new Set())
      setStepIndex((current) => Math.min(current + 1, flow.length - 1))
    } catch (uploadError) {
      setError(uploadError instanceof Error ? uploadError.message : 'Не удалось загрузить файл')
    } finally {
      setLoading(false)
    }
  }

  const loadAllRows = useCallback(async () => {
    if (!preview) {
      return
    }
    setLoading(true)
    setError(null)
    try {
      const collected: ImportPreviewRowDto[] = []
      let offset = 0
      let hasMore = true
      while (hasMore) {
        const page = await importCatalogPreviewRows({
          organizationId,
          previewToken: preview.previewToken,
          columnMapping,
          offset,
          limit: 100,
        })
        collected.push(...page.rows)
        offset += page.rows.length
        hasMore = page.hasMore
      }
      setRows(collected)
      setStepIndex((current) => Math.min(current + 1, flow.length - 1))
    } catch (rowsError) {
      setError(rowsError instanceof Error ? rowsError.message : 'Не удалось загрузить строки')
    } finally {
      setLoading(false)
    }
  }, [columnMapping, flow.length, organizationId, preview])

  const handleConfirmImport = async () => {
    if (!preview) {
      return
    }
    setLoading(true)
    setError(null)
    try {
      const result = await confirmCatalogImport({
        organizationId,
        previewToken: preview.previewToken,
        columnMapping,
        rowProductTypeOverrides,
        excludedRowNumbers: [...excludedRowNumbers],
        ...(mode.kind === 'update'
          ? { privateSupplierId: mode.supplierId }
          : {
              newSupplier: {
                legalName: legalName.trim(),
                inn: inn.replace(/\D/g, ''),
                displayLabel: displayLabel.trim() || null,
              },
            }),
      })
      setConfirmResult(result)
      setStepIndex((current) => Math.min(current + 1, flow.length - 1))
    } catch (confirmError) {
      setError(confirmError instanceof Error ? confirmError.message : 'Не удалось импортировать')
    } finally {
      setLoading(false)
    }
  }

  const toggleExcluded = (rowNumber: number) => {
    setExcludedRowNumbers((current) => {
      const next = new Set(current)
      if (next.has(rowNumber)) {
        next.delete(rowNumber)
      } else {
        next.add(rowNumber)
      }
      return next
    })
  }

  const confirmSuggestion = (row: ImportPreviewRowDto) => {
    const top = row.matchPreview.candidates[0]
    if (!top) {
      return
    }
    setRowProductTypeOverrides((current) => ({
      ...current,
      [row.rowNumber]: top.productTypeId,
    }))
  }

  const pickCategory = (rowNumber: number, productTypeId: number) => {
    setRowProductTypeOverrides((current) => ({
      ...current,
      [rowNumber]: productTypeId,
    }))
    setCategoryPickerRow(null)
    setCategorySearch('')
  }

  const filteredProductTypes = useMemo(() => {
    const query = categorySearch.trim().toLowerCase()
    if (!query) {
      return productTypes.filter((type) => type.active).slice(0, 50)
    }
    return productTypes
      .filter(
        (type) =>
          type.active &&
          (type.name.toLowerCase().includes(query) || type.code.toLowerCase().includes(query)),
      )
      .slice(0, 50)
  }, [categorySearch, productTypes])

  const columnsValid =
    columnMapping.name != null && columnMapping.price != null

  const companyValid = isValidInn(inn) && legalName.trim().length > 0

  const renderStepsBar = () => (
    <div className="rp-steps" aria-hidden="true">
      {flow.slice(0, stepsBeforeResult).map((_, index) => (
        <div key={index} className={stepIndex >= index ? 'rp-step rp-step--done' : 'rp-step'} />
      ))}
    </div>
  )

  const renderCompanyStep = () => (
    <>
      {renderStepsBar()}
      <label className="echat-field">
        <span className="echat-field__label">ИНН</span>
        <input
          className="echat-field__input"
          inputMode="numeric"
          value={inn}
          onChange={(event) => {
            setInn(event.target.value)
            setMatchMessage(null)
            setMatchTone(null)
          }}
          placeholder="10 или 12 цифр"
        />
      </label>
      <button
        type="button"
        className="echat-btn echat-btn--ghost"
        disabled={!isValidInn(inn) || matchLoading}
        onClick={() => void handleCheckInn()}
      >
        {matchLoading ? 'Проверяем…' : 'Проверить'}
      </button>
      {matchMessage ? (
        <p className={matchTone === 'ok' ? 'rp-match-ok' : 'rp-match-warn'} role="status">
          {matchMessage}
        </p>
      ) : null}
      <label className="echat-field">
        <span className="echat-field__label">Наименование</span>
        <input
          className="echat-field__input"
          value={legalName}
          onChange={(event) => setLegalName(event.target.value)}
          placeholder="ООО «Поставщик»"
        />
      </label>
      <label className="echat-field">
        <span className="echat-field__label">
          Отображаемое имя{' '}
          <span className="echat-muted" style={{ fontWeight: 500 }}>
            (необязательно)
          </span>
        </span>
        <input
          className="echat-field__input"
          value={displayLabel}
          onChange={(event) => setDisplayLabel(event.target.value)}
          placeholder="Как показывать в списке прайсов"
        />
      </label>
    </>
  )

  const renderFileStep = () => (
    <>
      {renderStepsBar()}
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPT_FILES}
        className="visually-hidden"
        onChange={(event) => void handleFileSelected(event.target.files?.[0] ?? null)}
      />
      <button
        type="button"
        className="rp-dropzone"
        disabled={loading}
        onClick={() => fileInputRef.current?.click()}
      >
        <div className="rp-dropzone__ico" aria-hidden="true">
          📄⬆
        </div>
        <p className="rp-dropzone__title">Загрузить XLSX / CSV / PDF / фото прайса</p>
        <p className="rp-dropzone__hint">Нажмите, чтобы выбрать файл</p>
      </button>
      <p className="rp-meta-line" style={{ justifyContent: 'center' }}>
        <span>Поддерживается: .xlsx · .csv · .pdf · фото</span>
      </p>
      {preview ? (
        <p className="echat-muted">
          Загружен: {preview.filename} · {preview.totalRows} строк
        </p>
      ) : null}
    </>
  )

  const renderColumnsStep = () => (
    <>
      {renderStepsBar()}
      {preview ? (
        <p className="echat-muted">
          {preview.filename} · {preview.totalRows} строк
          {preview.savedProfileApplied ? ' · профиль применён' : ''}
        </p>
      ) : null}
      {CATALOG_IMPORT_FIELDS.map((field) => {
        const source = proposedSourceForField(preview, field)
        return (
          <div key={field} className="rp-map-row">
            <span className="rp-map-row__field">{catalogImportFieldLabel(field)}</span>
            <select
              className="rp-map-row__select"
              value={columnMapping[field] ?? ''}
              onChange={(event) => {
                const value = event.target.value
                setColumnMapping((current) => ({
                  ...current,
                  [field]: value === '' ? null : Number(value),
                }))
              }}
            >
              <option value="">— не выбрано —</option>
              {(preview?.columns ?? []).map((column) => (
                <option key={column.index} value={column.index}>
                  {column.header || `Колонка ${column.index + 1}`}
                </option>
              ))}
            </select>
            {source ? <span className="rp-map-row__badge">{mappingSourceLabel(source)}</span> : null}
          </div>
        )
      })}
    </>
  )

  const renderReviewStep = () => (
    <>
      {renderStepsBar()}
      <p className="echat-muted">
        {activeRows.length} из {rows.length} строк · исключено {excludedSet.size}
      </p>
      <div className="rp-stack">
        {rows.map((row) => {
          const excluded = excludedSet.has(row.rowNumber)
          const decision = effectiveDecision(row, rowProductTypeOverrides, excludedSet)
          const category = rowCategoryLabel(row, rowProductTypeOverrides, productTypes)
          const canChange =
            !excluded &&
            (decision === 'auto' ||
              decision === 'suggested' ||
              decision === 'manual' ||
              decision === 'unmatched')
          const chipClass =
            decision === 'auto' || decision === 'manual'
              ? 'rp-catchip rp-catchip--matched'
              : decision === 'suggested'
                ? 'rp-catchip rp-catchip--suggested'
                : decision === 'unmatched'
                  ? 'rp-catchip rp-catchip--unmatched'
                  : 'rp-catchip'

          return (
            <div
              key={row.rowNumber}
              className={excluded ? 'rp-rev-row rp-rev-row--excluded' : 'rp-rev-row'}
            >
              <div className="rp-rev-row__body">
                <p className="rp-rev-row__name">{row.name || `Строка ${row.rowNumber}`}</p>
                <p className="rp-rev-row__sub">
                  {row.price ? `${row.price} ₽` : '—'}
                  {row.unit ? ` · ${row.unit}` : ''}
                  {row.brand ? ` · ${row.brand}` : ''}
                </p>
                <div className="rp-rev-row__cat">
                  {category ? <span className={chipClass}>{category}</span> : (
                    <span className="rp-catchip rp-catchip--unmatched">Без категории</span>
                  )}
                  <span className="echat-muted" style={{ fontSize: '0.6875rem' }}>
                    {importMatchDecisionLabel(decision === 'excluded' ? 'unmatched' : decision)}
                  </span>
                  {decision === 'suggested' && !excluded ? (
                    <button
                      type="button"
                      className="rp-catlink"
                      onClick={() => confirmSuggestion(row)}
                    >
                      подтвердить
                    </button>
                  ) : null}
                  {canChange ? (
                    <button
                      type="button"
                      className="rp-catlink"
                      onClick={() => {
                        setCategoryPickerRow(row.rowNumber)
                        setCategorySearch('')
                      }}
                    >
                      изменить
                    </button>
                  ) : null}
                </div>
              </div>
              <button
                type="button"
                className={excluded ? 'rp-rev-x rp-rev-x--on' : 'rp-rev-x'}
                aria-label={excluded ? 'Вернуть строку' : 'Исключить строку'}
                onClick={() => toggleExcluded(row.rowNumber)}
              >
                ✕
              </button>
            </div>
          )
        })}
      </div>
    </>
  )

  const renderConfirmStep = () => (
    <>
      {renderStepsBar()}
      <div className="rp-confirm-card">
        Вы загружаете прайс в каталог <b>«{catalogName}»</b>
      </div>
      <div>
        <div className="rp-conf-row">
          <span className="rp-conf-row__label">Файл</span>
          <span>{preview?.filename ?? '—'}</span>
        </div>
        <div className="rp-conf-row">
          <span className="rp-conf-row__label">Позиций</span>
          <span>{rows.length}</span>
        </div>
        <div className="rp-conf-row">
          <span className="rp-conf-row__label">К импорту</span>
          <span>{activeRows.length}</span>
        </div>
        <div className="rp-conf-row">
          <span className="rp-conf-row__label">Сопоставлено</span>
          <span>{matchedCount}</span>
        </div>
        <div className="rp-conf-row">
          <span className="rp-conf-row__label">Исключено</span>
          <span>{excludedSet.size}</span>
        </div>
      </div>
      <p className="echat-muted">
        {mode.kind === 'update'
          ? 'Цены обновятся, история изменений сохранится.'
          : 'Будет создан новый прайс поставщика в «Моих прайсах» Провизиума.'}
      </p>
    </>
  )

  const renderResultStep = () => (
    <>
      {renderStepsBar()}
      <div className="rp-result">
        <div className="rp-result__big">{confirmResult?.imported ?? 0}</div>
        <p className="echat-muted">позиций загружено</p>
        <div className="rp-result__breakdown">
          <span>✓ {confirmResult?.autoMatched ?? 0} сопоставлено</span>
          <span>◑ {confirmResult?.suggested ?? 0} по подсказке</span>
          <span>✕ {confirmResult?.unmatched ?? 0} без типа</span>
        </div>
      </div>
      <p className="rp-meta-line" style={{ justifyContent: 'center' }}>
        <span>
          {mode.kind === 'update'
            ? 'Прайс обновлён в Провизиуме'
            : 'Новый прайс добавлен в «Мои прайсы»'}
        </span>
      </p>
    </>
  )

  const title = (() => {
    switch (step) {
      case 'company':
        return 'Новый прайс — поставщик'
      case 'file':
        return mode.kind === 'update' ? `Обновить: ${catalogName}` : `Прайс: ${catalogName}`
      case 'columns':
        return 'Распознавание колонок'
      case 'review':
        return 'Проверка позиций'
      case 'confirm':
        return 'Подтверждение загрузки'
      case 'result':
        return mode.kind === 'update' ? 'Прайс обновлён' : 'Прайс добавлен'
      default:
        return 'Импорт прайса'
    }
  })()

  const subtitle = (() => {
    switch (step) {
      case 'company':
        return 'Чей прайс загружаем — карточка компании'
      case 'file':
        return mode.kind === 'update'
          ? 'Профиль колонок сохранён — распознаем автоматически'
          : 'Экспресс-режим: колонки распознаются автоматически'
      case 'columns':
        return preview
          ? `${preview.filename} · ${preview.totalRows} строк`
          : 'Сопоставьте колонки файла'
      case 'review':
        return 'Подтвердите категории, поправьте или исключите строки'
      case 'confirm':
        return 'Проверьте, куда и что загружаем'
      case 'result':
        return catalogName
      default:
        return 'Провизиум'
    }
  })()

  const renderBody = () => {
    switch (step) {
      case 'company':
        return renderCompanyStep()
      case 'file':
        return renderFileStep()
      case 'columns':
        return renderColumnsStep()
      case 'review':
        return renderReviewStep()
      case 'confirm':
        return renderConfirmStep()
      case 'result':
        return renderResultStep()
      default:
        return null
    }
  }

  const renderFooter = () => {
    if (step === 'company') {
      return (
        <>
          <button type="button" className="echat-btn echat-btn--ghost" onClick={onClose}>
            Отмена
          </button>
          <button
            type="button"
            className="echat-btn echat-btn--primary"
            disabled={!companyValid}
            onClick={goNext}
          >
            Дальше — файл прайса
          </button>
        </>
      )
    }

    if (step === 'file') {
      return (
        <>
          <button type="button" className="echat-btn echat-btn--ghost" onClick={goBack}>
            {mode.kind === 'new' ? 'Назад' : 'Отмена'}
          </button>
          <button
            type="button"
            className="echat-btn echat-btn--primary"
            disabled={!preview || loading}
            onClick={goNext}
          >
            Дальше
          </button>
        </>
      )
    }

    if (step === 'columns') {
      return (
        <>
          <button type="button" className="echat-btn echat-btn--ghost" onClick={goBack}>
            Назад
          </button>
          <button
            type="button"
            className="echat-btn echat-btn--primary"
            disabled={!columnsValid || loading}
            onClick={() => void loadAllRows()}
          >
            {loading ? 'Загрузка…' : 'Дальше — проверка'}
          </button>
        </>
      )
    }

    if (step === 'review') {
      return (
        <>
          <button type="button" className="echat-btn echat-btn--ghost" onClick={goBack}>
            Назад
          </button>
          <button type="button" className="echat-btn echat-btn--primary" onClick={goNext}>
            Дальше
          </button>
        </>
      )
    }

    if (step === 'confirm') {
      return (
        <>
          <button type="button" className="echat-btn echat-btn--ghost" onClick={goBack}>
            Назад
          </button>
          <button
            type="button"
            className="echat-btn echat-btn--primary"
            disabled={loading}
            onClick={() => void handleConfirmImport()}
          >
            {loading ? 'Загружаем…' : 'Загрузить прайс'}
          </button>
        </>
      )
    }

    return (
      <button type="button" className="echat-btn echat-btn--primary" onClick={onDone}>
        Готово
      </button>
    )
  }

  return (
    <div className="rp-wizard-overlay" role="dialog" aria-modal="true" aria-labelledby="import-wizard-title">
      <header className="rp-wizard__head">
        <h2 id="import-wizard-title" className="rp-wizard__title">
          {title}
        </h2>
        <p className="rp-wizard__sub">{subtitle}</p>
      </header>

      <div className="rp-wizard__body">
        {error ? (
          <p className="echat-error" role="alert">
            {error}
          </p>
        ) : null}
        {renderBody()}
      </div>

      <footer className="rp-wizard__foot">{renderFooter()}</footer>

      {categoryPickerRow != null ? (
        <div className="echat-modal" role="dialog" aria-modal="true" aria-labelledby="cat-picker-title">
          <div
            className="echat-modal__backdrop"
            onClick={() => {
              setCategoryPickerRow(null)
              setCategorySearch('')
            }}
          />
          <div className="echat-modal__panel">
            <h3 id="cat-picker-title" className="echat-modal__title">
              Категория продукции
            </h3>
            <label className="echat-field">
              <span className="echat-field__label">Поиск</span>
              <input
                className="echat-field__input"
                type="search"
                value={categorySearch}
                onChange={(event) => setCategorySearch(event.target.value)}
                placeholder="Начните вводить название…"
                autoFocus
              />
            </label>
            <ul className="rp-cat-list">
              {filteredProductTypes.map((type) => (
                <li key={type.id}>
                  <button
                    type="button"
                    className="rp-cat-opt"
                    onClick={() => pickCategory(categoryPickerRow, type.id)}
                  >
                    {type.name}
                  </button>
                </li>
              ))}
            </ul>
            {filteredProductTypes.length === 0 ? (
              <p className="echat-muted">Ничего не найдено</p>
            ) : null}
            <button
              type="button"
              className="echat-btn echat-btn--ghost"
              onClick={() => {
                setCategoryPickerRow(null)
                setCategorySearch('')
              }}
            >
              Закрыть
            </button>
          </div>
        </div>
      ) : null}
    </div>
  )
}
